/**
 * Form Validation Helpers
 * 
 * Helper functions for form validation and completion calculation
 */

import { JobPostFormData } from '../schemas/jobPostValidation';

/**
 * Validate Step 1 completion
 */
export const validateStep1Complete = (data: JobPostFormData['step1'] | null | undefined): boolean => {
  if (!data) return false;
  
  return !!(
    data.title &&
    data.title.trim().length > 0 &&
    data.position &&
    data.position.trim().length > 0 &&
    data.description &&
    data.description.trim().length > 0 &&
    data.numberOfOpenings &&
    data.numberOfOpenings > 0
  );
};

/**
 * Calculate completion percentage for Step 1
 */
export const calculateCompletionPercentage = (data: JobPostFormData['step1'] | null | undefined): number => {
  if (!data) return 0;
  
  const fields = [
    'title',
    'position',
    'description',
    'numberOfOpenings',
    'noticePeriod',
    'ctc',
    'endDate',
  ];
  
  const completedFields = fields.filter((field) => {
    const value = data[field as keyof typeof data];
    if (field === 'numberOfOpenings') {
      return value !== null && value !== undefined && (value as number) > 0;
    }
    return value !== null && value !== undefined && String(value).trim().length > 0;
  });
  
  return Math.round((completedFields.length / fields.length) * 100);
};

/**
 * Validate Step 2 completion
 */
export const validateStep2Complete = (data: JobPostFormData['step2'] | null | undefined): boolean => {
  if (!data) return false;
  
  return !!(
    data.requirements &&
    data.requirements.length > 0 &&
    data.requirements.some((req) => req.mustMatch === true)
  );
};

/**
 * Calculate completion percentage for Step 2
 */
export const calculateStep2CompletionPercentage = (data: JobPostFormData['step2'] | null | undefined): number => {
  if (!data || !data.requirements || data.requirements.length === 0) return 0;
  
  const hasMustMatch = data.requirements.some((req) => req.mustMatch === true);
  return hasMustMatch ? 100 : 50;
};

/**
 * Validate Step 3 completion
 */
export const validateStep3Complete = (data: JobPostFormData['step3'] | null | undefined): boolean => {
  if (!data || !data.interviewRounds || data.interviewRounds.length === 0) return false;
  
  // Normalize round IDs for comparison (handle Profile Matching special case)
  const normalizeRoundId = (round: any): string | null => {
    const id = round.interviewRoundId || round._id || round._displayMetadata?._id;
    if (!id) return null;
    
    // Normalize Profile Matching IDs (can be 'profile-match' or various formats)
    const idStr = String(id).toLowerCase().trim();
    if (idStr === 'profile-match' || idStr === 'profilematching') {
      return 'profile-match';
    }
    
    return id;
  };
  
  // Check for duplicate rounds
  const roundsWithIds = data.interviewRounds.filter(round => {
    return normalizeRoundId(round) !== null;
  });
  
  const uniqueIds = new Set<string>();
  for (const round of roundsWithIds) {
    const id = normalizeRoundId(round);
    if (id) {
      if (uniqueIds.has(id)) {
        return false; // Duplicate found
      }
      uniqueIds.add(id);
    }
  }
  
  return data.interviewRounds.length >= 1; // At least one round and no duplicates
};

/**
 * Calculate completion percentage for Step 3
 */
export const calculateStep3CompletionPercentage = (data: JobPostFormData['step3'] | null | undefined): number => {
  if (!data || !data.interviewRounds) return 0;
  return data.interviewRounds.length > 0 ? 100 : 0;
};

/**
 * Validate Step 5 completion
 */
export const validateStep5Complete = (data: JobPostFormData['step5'] | null | undefined): boolean => {
  if (!data) return false;
  if (data.onlineInterview && (!data.interviewers || data.interviewers.length === 0)) {
    return false; // Online interview requires at least one interviewer
  }
  return true;
};

/**
 * Calculate completion percentage for Step 5
 */
export const calculateStep5CompletionPercentage = (data: JobPostFormData['step5'] | null | undefined): number => {
  if (!data) return 0;
  let score = 0;
  let totalFields = 2; // onlineInterview, interviewers

  // onlineInterview is always set (defaults to true)
  score++;
  
  // Interviewers are required only for online interviews
  if (data.onlineInterview) {
    if (data.interviewers && data.interviewers.length > 0) {
      score++;
    }
  } else {
    // In-person interviews don't require interviewers
    score++;
  }

  return Math.round((score / totalFields) * 100);
};

